
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { generateJobContractPDF } from "@/utils/jobContractPDFGenerator";
import { PersonalInfoSection } from "./forms/PersonalInfoSection";
import { AddressSection } from "./forms/AddressSection";
import { InsuranceSection } from "./forms/InsuranceSection";
import { BankingSection } from "./forms/BankingSection";
import { WorkPermitSection } from "./forms/WorkPermitSection";

interface JobContractEmployee {
  id: string;
  name: string;
  firstname?: string;
  lastname?: string;
  email: string;
  birth_date?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  ahv_number?: string;
  iban?: string;
  bank_name?: string;
  hourly_rate?: number;
  monthly_salary?: number;
  health_insurance_provider?: string;
  accident_insurance_number?: string;
  pension_fund_number?: string;
  bank_clearing_number?: string;
  tax_municipality?: string;
  tax_canton?: string;
  marital_status?: string;
  gender?: string;
  nationality?: string;
  has_children?: boolean;
  kinderzulagen_eligible?: boolean;
  work_permit_type?: string;
  work_permit_expires?: string;
}

interface JobContractFormData {
  employee_id: string;
  contract_type: "hourly" | "monthly";
  start_date: string;
  end_date?: string;
  hourly_rate?: number;
  monthly_salary?: number;
  working_hours_per_week: number;
  probation_period_months: number;
  vacation_days: number;
  notice_period_days: number;
  job_title: string;
  department: string;
  job_description: string;
}

export function JobContractForm() {
  const [employees, setEmployees] = useState<JobContractEmployee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<JobContractEmployee | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewEmployeeForm, setShowNewEmployeeForm] = useState(false);
  const { toast } = useToast();

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<JobContractFormData>({
    defaultValues: {
      contract_type: "hourly",
      working_hours_per_week: 42,
      probation_period_months: 3,
      vacation_days: 25,
      notice_period_days: 30,
    }
  });

  const { register: registerEmployee, handleSubmit: handleEmployeeSubmit, watch: watchEmployee, setValue: setEmployeeValue, reset: resetEmployee, formState: { errors: employeeErrors } } = useForm();

  const contractType = watch("contract_type");

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      // Check admin status first
      const { data: isAdmin } = await supabase.rpc('check_is_admin');
      if (!isAdmin) {
        toast({
          title: "Zugriff verweigert",
          description: "Sie haben keine Berechtigung für diese Aktion",
          variant: "destructive",
        });
        return;
      }

      // Fetch employees from the existing employees table
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          name,
          firstname,
          lastname,
          email,
          birth_date,
          address,
          street_address,
          city,
          postal_code,
          country,
          ahv_number,
          iban,
          bank_name,
          hourly_rate,
          monthly_salary,
          health_insurance_provider,
          accident_insurance_number,
          pension_fund_number,
          bank_clearing_number,
          tax_municipality,
          tax_canton,
          marital_status,
          gender,
          nationality,
          has_children,
          kinderzulagen_eligible,
          work_permit_type,
          work_permit_expires
        `)
        .eq('status', 'active')
        .order('lastname', { ascending: true });
      
      if (error) {
        console.error('Error fetching employees:', error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Mitarbeiter",
          variant: "destructive",
        });
        return;
      }

      // Transform the data to match our interface
      const formattedEmployees: JobContractEmployee[] = (data || []).map(emp => ({
        id: emp.id,
        name: emp.name,
        firstname: emp.firstname || '',
        lastname: emp.lastname || '',
        email: emp.email,
        birth_date: emp.birth_date || '',
        address: emp.street_address || emp.address || '',
        city: emp.city || '',
        postal_code: emp.postal_code || '',
        country: emp.country || 'Switzerland',
        ahv_number: emp.ahv_number || '',
        iban: emp.iban || '',
        bank_name: emp.bank_name || '',
        hourly_rate: emp.hourly_rate || 0,
        monthly_salary: emp.monthly_salary || 0,
        health_insurance_provider: emp.health_insurance_provider || '',
        accident_insurance_number: emp.accident_insurance_number || '',
        pension_fund_number: emp.pension_fund_number || '',
        bank_clearing_number: emp.bank_clearing_number || '',
        tax_municipality: emp.tax_municipality || '',
        tax_canton: emp.tax_canton || '',
        marital_status: emp.marital_status || 'single',
        gender: emp.gender || 'prefer_not_to_say',
        nationality: emp.nationality || 'Swiss',
        has_children: emp.has_children || false,
        kinderzulagen_eligible: emp.kinderzulagen_eligible || false,
        work_permit_type: emp.work_permit_type || undefined,
        work_permit_expires: emp.work_permit_expires || undefined,
      }));

      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
      setValue("employee_id", employee.id);
      if (employee.hourly_rate) {
        setValue("hourly_rate", employee.hourly_rate);
      }
      if (employee.monthly_salary) {
        setValue("monthly_salary", employee.monthly_salary);
      }
    }
  };

  const onCreateEmployee = async (data: any) => {
    try {
      const { error } = await supabase
        .from('employees')
        .insert([{
          ...data,
          name: `${data.firstname} ${data.lastname}`,
          status: 'active',
          role: 'employee'
        }]);

      if (error) {
        console.error('Error creating employee:', error);
        toast({
          title: "Fehler",
          description: "Fehler beim Erstellen des Mitarbeiters",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Erfolg",
        description: "Mitarbeiter wurde erfolgreich erstellt",
      });

      await fetchEmployees();
      setShowNewEmployeeForm(false);
      resetEmployee();
    } catch (error) {
      console.error('Error creating employee:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Erstellen des Mitarbeiters",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: JobContractFormData) => {
    if (!selectedEmployee) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie einen Mitarbeiter aus",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Convert JobContractEmployee to the format expected by PDF generator
      const employeeForPDF = {
        id: selectedEmployee.id,
        name: selectedEmployee.name,
        firstname: selectedEmployee.firstname || '',
        lastname: selectedEmployee.lastname || '',
        email: selectedEmployee.email,
        birth_date: selectedEmployee.birth_date || '',
        address: selectedEmployee.address || '',
        city: selectedEmployee.city || '',
        postal_code: selectedEmployee.postal_code || '',
        country: selectedEmployee.country || 'Switzerland',
        ahv_number: selectedEmployee.ahv_number || '',
        iban: selectedEmployee.iban || '',
        bank_name: selectedEmployee.bank_name || '',
        hourly_rate: selectedEmployee.hourly_rate || 0,
        monthly_salary: selectedEmployee.monthly_salary || 0,
        health_insurance_provider: selectedEmployee.health_insurance_provider || '',
        accident_insurance_number: selectedEmployee.accident_insurance_number || '',
        pension_fund_number: selectedEmployee.pension_fund_number || '',
        bank_clearing_number: selectedEmployee.bank_clearing_number || '',
        tax_municipality: selectedEmployee.tax_municipality || '',
        tax_canton: selectedEmployee.tax_canton || '',
        marital_status: selectedEmployee.marital_status || 'single',
        gender: selectedEmployee.gender || 'prefer_not_to_say',
        nationality: selectedEmployee.nationality || 'Swiss',
        has_children: selectedEmployee.has_children || false,
        kinderzulagen_eligible: selectedEmployee.kinderzulagen_eligible || false,
        work_permit_type: selectedEmployee.work_permit_type || undefined,
        work_permit_expires: selectedEmployee.work_permit_expires || undefined,
      };
      
      // Generate PDF
      await generateJobContractPDF({
        ...data,
        employee: employeeForPDF,
        contract_id: `temp-${Date.now()}`, // Temporary ID until database is available
      });

      toast({
        title: "Erfolg",
        description: "Arbeitsvertrag wurde erfolgreich als PDF generiert",
      });

      reset();
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error creating contract:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Erstellen des Vertrags",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Neuen Arbeitsvertrag erstellen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <Button 
              variant={!showNewEmployeeForm ? "default" : "outline"}
              onClick={() => {
                setShowNewEmployeeForm(false);
                fetchEmployees();
              }}
            >
              Existierenden Mitarbeiter wählen
            </Button>
            <Button 
              variant={showNewEmployeeForm ? "default" : "outline"}
              onClick={() => setShowNewEmployeeForm(true)}
            >
              Neuen Mitarbeiter erstellen
            </Button>
          </div>

          {!showNewEmployeeForm ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Mitarbeiter auswählen</label>
                <Select onValueChange={handleEmployeeSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Mitarbeiter wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.firstname} {employee.lastname} ({employee.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedEmployee && (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Contract Type */}
                  <div className="space-y-2">
                    <label>Vertragstyp</label>
                    <Select onValueChange={(value: "hourly" | "monthly") => setValue("contract_type", value)} defaultValue="hourly">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Stundenlohn</SelectItem>
                        <SelectItem value="monthly">Monatslohn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Job Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label>Stellenbezeichnung</label>
                      <Input {...register("job_title", { required: "Stellenbezeichnung ist erforderlich" })} />
                      {errors.job_title && <p className="text-red-500 text-sm">{errors.job_title.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <label>Abteilung</label>
                      <Input {...register("department")} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label>Tätigkeitsbeschreibung</label>
                    <Input {...register("job_description")} placeholder="Kurze Beschreibung der Hauptaufgaben" />
                  </div>

                  {/* Contract Terms */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label>Vertragsbeginn</label>
                      <Input type="date" {...register("start_date", { required: "Startdatum ist erforderlich" })} />
                      {errors.start_date && <p className="text-red-500 text-sm">{errors.start_date.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <label>Vertragsende (optional)</label>
                      <Input type="date" {...register("end_date")} />
                    </div>
                  </div>

                  {/* Salary Information */}
                  {contractType === "hourly" ? (
                    <div className="space-y-2">
                      <label>Stundenlohn (CHF)</label>
                      <Input
                        type="number"
                        step="0.01"
                        {...register("hourly_rate", { 
                          required: contractType === "hourly" ? "Stundenlohn ist erforderlich" : false,
                          min: { value: 0, message: "Stundenlohn muss positiv sein" }
                        })}
                      />
                      {errors.hourly_rate && <p className="text-red-500 text-sm">{errors.hourly_rate.message}</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label>Monatslohn (CHF)</label>
                      <Input
                        type="number"
                        step="0.01"
                        {...register("monthly_salary", { 
                          required: contractType === "monthly" ? "Monatslohn ist erforderlich" : false,
                          min: { value: 0, message: "Monatslohn muss positiv sein" }
                        })}
                      />
                      {errors.monthly_salary && <p className="text-red-500 text-sm">{errors.monthly_salary.message}</p>}
                    </div>
                  )}

                  {/* Working Conditions */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label>Wochenstunden</label>
                      <Input
                        type="number"
                        step="0.5"
                        {...register("working_hours_per_week", { 
                          required: "Wochenstunden sind erforderlich",
                          min: { value: 1, message: "Mindestens 1 Stunde pro Woche" },
                          max: { value: 48, message: "Maximal 48 Stunden pro Woche" }
                        })}
                      />
                      {errors.working_hours_per_week && <p className="text-red-500 text-sm">{errors.working_hours_per_week.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <label>Probezeit (Monate)</label>
                      <Input
                        type="number"
                        {...register("probation_period_months", { 
                          min: { value: 0, message: "Probezeit muss positiv sein" },
                          max: { value: 12, message: "Maximal 12 Monate Probezeit" }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <label>Ferientage</label>
                      <Input
                        type="number"
                        {...register("vacation_days", { 
                          min: { value: 20, message: "Mindestens 20 Ferientage" },
                          max: { value: 50, message: "Maximal 50 Ferientage" }
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label>Kündigungsfrist (Tage)</label>
                    <Input
                      type="number"
                      {...register("notice_period_days", { 
                        min: { value: 7, message: "Mindestens 7 Tage Kündigungsfrist" }
                      })}
                    />
                  </div>

                  <Button type="submit" disabled={loading || !selectedEmployee} className="w-full">
                    {loading ? "Erstelle Vertrag..." : "Arbeitsvertrag erstellen und PDF generieren"}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleEmployeeSubmit(onCreateEmployee)} className="space-y-6">
              <PersonalInfoSection 
                register={registerEmployee}
                errors={employeeErrors}
                setValue={setEmployeeValue}
                watch={watchEmployee}
              />
              
              <AddressSection 
                register={registerEmployee}
                errors={employeeErrors}
                setValue={setEmployeeValue}
                watch={watchEmployee}
              />
              
              <InsuranceSection 
                register={registerEmployee}
                errors={employeeErrors}
                setValue={setEmployeeValue}
                watch={watchEmployee}
              />
              
              <BankingSection 
                register={registerEmployee}
                errors={employeeErrors}
              />
              
              <WorkPermitSection 
                register={registerEmployee}
                errors={employeeErrors}
                setValue={setEmployeeValue}
                watch={watchEmployee}
              />

              <Button type="submit" className="w-full">
                Mitarbeiter erstellen
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
